import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { AvatarNameVariant, AvatarSize } from './types'

@safeCustomElement('mdc-avatar')
export class MdcAvatar extends LitElement {
  @property({ type: String, reflect: true }) size: AvatarSize = 'md'
  @property({ type: String }) src = ''
  @property({ type: String }) alt = ''
  @property({ type: String }) text = ''
  @property({ type: Boolean, attribute: 'is-more' }) isMore = false
  @property({ type: String, reflect: true }) title = ''
  @property({ type: String, reflect: true }) subtitle = ''
  @property({ type: String, reflect: true, attribute: 'name-variant' })
  nameVariant: AvatarNameVariant = 'bold'
  // Opt-in: let the name/role wrap to multiple lines instead of truncating with an ellipsis.
  // Used where the avatar sits in a fixed-width column with no horizontal scroll (e.g. a social
  // card's single "Posted by" person), so a long name isn't cut off. Default (unset) is unchanged.
  @property({ type: Boolean, reflect: true, attribute: 'wrap-name' }) wrapName = false

  static styles = css`
		:host {
			font-family: var(--typography-font-family-sans);
			/* Avatar Design Tokens */
			--mdc-avatar-border-radius: var(--radius-full, 9999px);
			--mdc-avatar-border-width: 1px;

			/* Avatar Default State */
			--mdc-avatar-color: var(--color-functional-gray-400, #a3a3a3);
			--mdc-avatar-border-color: var(--color-functional-gray-75, #f0f0f0);
			--mdc-avatar-background: var(--color-functional-gray-25, #fafafa);

			/* Avatar More State */
			--mdc-avatar-more-color: var(--color-functional-gray-300, #d4d4d4);
			--mdc-avatar-more-background: var(--color-functional-gray-50, #f5f5f5);

			/* Avatar Name Styles */
			--mdc-avatar-name-font-size: var(--text-100, 1rem);
			--mdc-avatar-name-color: var(--color-functional-gray-black, #000000);
			--mdc-avatar-name-line-height: 1.3;
			--mdc-avatar-name-font-family-bold: var(--typography-font-family-sans);
			--mdc-avatar-name-font-weight-bold: 600;
			--mdc-avatar-name-font-family-subtle: var(--typography-font-family-sans);
			--mdc-avatar-name-font-weight-subtle: 450;

			/* Avatar Role/Subtitle Styles */
			--mdc-avatar-role-font-family: var(--typography-font-family-sans);
			--mdc-avatar-role-font-size: var(--text-075, 0.75rem);
			--mdc-avatar-role-color: var(--color-functional-gray-400, #a3a3a3);
			--mdc-avatar-role-font-weight: 450;
			--mdc-avatar-role-line-height: 1.3;
			--mdc-avatar-content-gap: var(--spacing-012-and-half, 0.125rem);

			/* Avatar Wrapper Gaps */
			--mdc-avatar-wrapper-gap-2xs: var(--spacing-050, 0.5rem);
			--mdc-avatar-wrapper-gap-xs: var(--spacing-050, 0.5rem);
			--mdc-avatar-wrapper-gap-sm: var(--spacing-050, 0.5rem);
			--mdc-avatar-wrapper-gap-md: var(--spacing-075, 0.75rem);
			--mdc-avatar-wrapper-gap-lg: var(--spacing-075, 0.75rem);
			--mdc-avatar-wrapper-gap-xl: var(--spacing-075, 0.75rem);
			--mdc-avatar-wrapper-gap-2xl: var(--spacing-075, 0.75rem);
			--mdc-avatar-wrapper-gap-3xl: var(--spacing-075, 0.75rem);

			/* Avatar Size 2xs */
			--mdc-avatar-size-2xs-width: 1.5rem;
			--mdc-avatar-size-2xs-height: 1.5rem;
			--mdc-avatar-size-2xs-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-2xs-font-size: var(--text-050, 0.5rem);
			--mdc-avatar-size-2xs-font-weight: 450;
			--mdc-avatar-size-2xs-line-height: 1.2;

			/* Avatar Size xs */
			--mdc-avatar-size-xs-width: 2rem;
			--mdc-avatar-size-xs-height: 2rem;
			--mdc-avatar-size-xs-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-xs-font-size: var(--text-062-and-half, 0.625rem);
			--mdc-avatar-size-xs-font-weight: 450;
			--mdc-avatar-size-xs-line-height: 1.2;

			/* Avatar Size sm */
			--mdc-avatar-size-sm-width: 2.5rem;
			--mdc-avatar-size-sm-height: 2.5rem;
			--mdc-avatar-size-sm-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-sm-font-size: var(--text-075, 0.75rem);
			--mdc-avatar-size-sm-font-weight: 450;
			--mdc-avatar-size-sm-line-height: 1.3;

			/* Avatar Size md */
			--mdc-avatar-size-md-width: 3rem;
			--mdc-avatar-size-md-height: 3rem;
			--mdc-avatar-size-md-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-md-font-size: var(--text-075, 0.75rem);
			--mdc-avatar-size-md-font-weight: 450;
			--mdc-avatar-size-md-line-height: 1.3;

			/* Avatar Size lg */
			--mdc-avatar-size-lg-width: 4.5rem;
			--mdc-avatar-size-lg-height: 4.5rem;
			--mdc-avatar-size-lg-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-lg-font-size: var(--text-100, 1rem);
			--mdc-avatar-size-lg-font-weight: 450;
			--mdc-avatar-size-lg-line-height: 1.3;

			/* Avatar Size xl */
			--mdc-avatar-size-xl-width: 6rem;
			--mdc-avatar-size-xl-height: 6rem;
			--mdc-avatar-size-xl-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-xl-font-size: var(--text-100, 1rem);
			--mdc-avatar-size-xl-font-weight: 450;
			--mdc-avatar-size-xl-line-height: 1.3;

			/* Avatar Size 2xl */
			--mdc-avatar-size-2xl-width: 7.5rem;
			--mdc-avatar-size-2xl-height: 7.5rem;
			--mdc-avatar-size-2xl-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-2xl-font-size: var(--text-100, 1rem);
			--mdc-avatar-size-2xl-font-weight: 450;
			--mdc-avatar-size-2xl-line-height: 1.3;

			/* Avatar Size 3xl */
			--mdc-avatar-size-3xl-width: 9rem;
			--mdc-avatar-size-3xl-height: 9rem;
			--mdc-avatar-size-3xl-font-family: var(--typography-font-family-sans);
			--mdc-avatar-size-3xl-font-size: var(--text-125, 1.25rem);
			--mdc-avatar-size-3xl-font-weight: 450;
			--mdc-avatar-size-3xl-line-height: 1.3;
		}

		:host {
			display: inline-block;
		}

		:host([title]) {
			display: block;
		}

		.mdc-avatar,
		.mdc-avatar *,
		.mdc-avatar *::before,
		.mdc-avatar *::after {
			box-sizing: border-box;
		}

		.mdc-avatar {
			display: flex;
			align-items: center;
			justify-content: center;
			position: relative;
			border-radius: var(--mdc-avatar-border-radius);
		}

		/* Layout wrapper when name/role is provided */
		.mdc-avatar__wrapper {
			display: flex;
			align-items: center;
		}

		.mdc-avatar__avatar {
			flex-shrink: 0;
		}

		.mdc-avatar__content {
			display: flex;
			flex-direction: column;
			gap: var(--mdc-avatar-content-gap);
		}

		.mdc-avatar__name {
			font-size: var(--mdc-avatar-name-font-size);
			color: var(--mdc-avatar-name-color);
			line-height: var(--mdc-avatar-name-line-height);
			white-space: nowrap;
			margin: 0;
			padding: 0;
		}

		/* Opt-in wrapping (wrap-name): let long names/roles break instead of truncating. */
		:host([wrap-name]) .mdc-avatar__name,
		:host([wrap-name]) .mdc-avatar__role {
			white-space: normal;
			overflow: visible;
			text-overflow: clip;
		}

		.mdc-avatar__role {
			font-family: var(--mdc-avatar-role-font-family);
			font-size: var(--mdc-avatar-role-font-size);
			color: var(--mdc-avatar-role-color);
			font-weight: var(--mdc-avatar-role-font-weight);
			line-height: var(--mdc-avatar-role-line-height);
			font-style: normal;
			white-space: nowrap;
			margin: 0;
			padding: 0;
		}

		/* Size gaps for wrapper layout */
		:host([title][size="2xs"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-2xs);
		}

		:host([title][size="xs"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-xs);
		}

		:host([title][size="sm"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-sm);
		}

		:host([title][size="md"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-md);
		}

		:host([title][size="lg"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-lg);
		}

		:host([title][size="xl"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-xl);
		}

		:host([title][size="2xl"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-2xl);
		}

		:host([title][size="3xl"]) .mdc-avatar__wrapper {
			gap: var(--mdc-avatar-wrapper-gap-3xl);
		}

		/* Name variant styles */
		:host([name-variant="bold"]) .mdc-avatar__name {
			font-family: var(--mdc-avatar-name-font-family-bold);
			font-weight: var(--mdc-avatar-name-font-weight-bold);
		}

		:host([name-variant="subtle"]) .mdc-avatar__name {
			font-family: var(--mdc-avatar-name-font-family-subtle);
			font-weight: var(--mdc-avatar-name-font-weight-subtle);
		}

		.mdc-avatar:not(.mdc-avatar--more) {
			color: var(--mdc-avatar-color);
			border: var(--mdc-avatar-border-width) solid var(--mdc-avatar-border-color);
			background-color: var(--mdc-avatar-background);
		}

		.mdc-avatar--more {
			color: var(--mdc-avatar-more-color);
			background-color: var(--mdc-avatar-more-background);
		}

		.mdc-avatar img {
			display: block;
			width: 100%;
			height: 100%;
			position: absolute;
			border-radius: var(--mdc-avatar-border-radius);
			object-fit: cover;
			inset: 0;
		}

		.mdc-avatar--size-2xs {
			width: var(--mdc-avatar-size-2xs-width);
			height: var(--mdc-avatar-size-2xs-height);
			font-family: var(--mdc-avatar-size-2xs-font-family);
			font-size: var(--mdc-avatar-size-2xs-font-size);
			font-weight: var(--mdc-avatar-size-2xs-font-weight);
			line-height: var(--mdc-avatar-size-2xs-line-height);
		}

		.mdc-avatar--size-xs {
			width: var(--mdc-avatar-size-xs-width);
			height: var(--mdc-avatar-size-xs-height);
			font-family: var(--mdc-avatar-size-xs-font-family);
			font-size: var(--mdc-avatar-size-xs-font-size);
			font-weight: var(--mdc-avatar-size-xs-font-weight);
			line-height: var(--mdc-avatar-size-xs-line-height);
		}

		.mdc-avatar--size-sm {
			width: var(--mdc-avatar-size-sm-width);
			height: var(--mdc-avatar-size-sm-height);
			font-family: var(--mdc-avatar-size-sm-font-family);
			font-size: var(--mdc-avatar-size-sm-font-size);
			font-weight: var(--mdc-avatar-size-sm-font-weight);
			line-height: var(--mdc-avatar-size-sm-line-height);
		}

		.mdc-avatar--size-md {
			width: var(--mdc-avatar-size-md-width);
			height: var(--mdc-avatar-size-md-height);
			font-family: var(--mdc-avatar-size-md-font-family);
			font-size: var(--mdc-avatar-size-md-font-size);
			font-weight: var(--mdc-avatar-size-md-font-weight);
			line-height: var(--mdc-avatar-size-md-line-height);
		}

		.mdc-avatar--size-lg {
			width: var(--mdc-avatar-size-lg-width);
			height: var(--mdc-avatar-size-lg-height);
			font-family: var(--mdc-avatar-size-lg-font-family);
			font-size: var(--mdc-avatar-size-lg-font-size);
			font-weight: var(--mdc-avatar-size-lg-font-weight);
			line-height: var(--mdc-avatar-size-lg-line-height);
		}

		.mdc-avatar--size-xl {
			width: var(--mdc-avatar-size-xl-width);
			height: var(--mdc-avatar-size-xl-height);
			font-family: var(--mdc-avatar-size-xl-font-family);
			font-size: var(--mdc-avatar-size-xl-font-size);
			font-weight: var(--mdc-avatar-size-xl-font-weight);
			line-height: var(--mdc-avatar-size-xl-line-height);
		}

		.mdc-avatar--size-2xl {
			width: var(--mdc-avatar-size-2xl-width);
			height: var(--mdc-avatar-size-2xl-height);
			font-family: var(--mdc-avatar-size-2xl-font-family);
			font-size: var(--mdc-avatar-size-2xl-font-size);
			font-weight: var(--mdc-avatar-size-2xl-font-weight);
			line-height: var(--mdc-avatar-size-2xl-line-height);
		}

		.mdc-avatar--size-3xl {
			width: var(--mdc-avatar-size-3xl-width);
			height: var(--mdc-avatar-size-3xl-height);
			font-family: var(--mdc-avatar-size-3xl-font-family);
			font-size: var(--mdc-avatar-size-3xl-font-size);
			font-weight: var(--mdc-avatar-size-3xl-font-weight);
			line-height: var(--mdc-avatar-size-3xl-line-height);
		}
	`

  render() {
    const avatarClassName = `mdc-avatar mdc-avatar--size-${this.size}${this.isMore ? ' mdc-avatar--more' : ''}`
    const hasTitle = this.title !== undefined && this.title !== null && this.title !== ''

    // Render with title/subtitle wrapper if title is provided
    if (hasTitle) {
      return html`
				<div class="mdc-avatar__wrapper">
					<div class="mdc-avatar__avatar">
						<div class=${avatarClassName}>
							${this.src ? html`<img src=${this.src} alt=${this.alt || ''} loading="lazy" />` : ''}
							${this.text ? html`<span>${this.text}</span>` : ''}
						</div>
					</div>
					<div class="mdc-avatar__content">
						<p class="mdc-avatar__name">${this.title}</p>
						${this.subtitle ? html`<p class="mdc-avatar__role">${this.subtitle}</p>` : ''}
					</div>
				</div>
			`
    }

    // Render standalone avatar if no title
    return html`
			<div class=${avatarClassName}>
				${this.src ? html`<img src=${this.src} alt=${this.alt || ''} loading="lazy" />` : ''}
				${this.text ? html`<span>${this.text}</span>` : ''}
			</div>
		`
  }
}
